import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, MapPin, User, Clock, FileText, Activity, Phone, Home, Briefcase, Upload, Image as ImageIcon, Video, Trash2, Map as MapIcon, UserPlus } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { getPropertyRawCoordinates } from '../../../models/externalModel';
import { getJobActivities, addJobActivity, createAssignment, getLocalWorkers } from '../../../models/mmsModel';
import { useStatusMapper } from '../../../hooks/useMmsLookups';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../../config';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icons in Leaflet with React 18
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

/**
 * Comprehensive Job Card Detail Modal with Tabbed Interface
 * Tabs: Overview, Events/History, Customer Info, Property Details
 */
const JobCardDetailModal = ({ job: initialJob, onClose, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [job] = useState(initialJob);
  // null = not fetched yet; [] = fetched but none
  const [activities, setActivities] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rawCoordinates, setRawCoordinates] = useState(null);
  const [loadingRawCoords, setLoadingRawCoords] = useState(false);
  const [activityForm, setActivityForm] = useState({
    description: '',
    activity_type: 'field_note',
    files: []
  });
  const [submittingActivity, setSubmittingActivity] = useState(false);
  
  // Assignment tab state
  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState('');
  const [assignmentNotes, setAssignmentNotes] = useState('');
  const [submittingAssignment, setSubmittingAssignment] = useState(false);
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  
  const { getOmStatusName, getPriorityName, getApplicationTypeName, getStationName } = useStatusMapper();

  const workersById = useMemo(() => {
    const map = new Map();
    (workers || []).forEach((w) => {
      if (w?.worker_id != null) {
        map.set(String(w.worker_id), w);
      }
    });
    return map;
  }, [workers]);

  // Log job data on mount and when it changes
  useEffect(() => {
    // Removed logging
  }, [job]);

  const fetchWorkers = useCallback(async () => {
    try {
      setLoadingWorkers(true);
      const response = await getLocalWorkers();
      if (response && response.data) {
        setWorkers(response.data.workers || []);
      }
    } catch (err) {
      console.error('Error fetching workers:', err);
      toast.error('Failed to load workers');
    } finally {
      setLoadingWorkers(false);
    }
  }, []);

  const fetchRawCoordinates = useCallback(async () => {
    if (!job.PROPERTY_ID) return;
    
    try {
      setLoadingRawCoords(true);
      const rawData = await getPropertyRawCoordinates(job.PROPERTY_ID, job.APPLICATION_ID);
      setRawCoordinates(rawData);
    } catch (err) {
      console.error('Error fetching raw coordinates:', err);
      // Don't show error toast - raw coordinates are supplementary info
    } finally {
      setLoadingRawCoords(false);
    }
  }, [job.APPLICATION_ID, job.PROPERTY_ID]);

  const fetchActivities = useCallback(async () => {
    if (!job.JOBCARD_NO) {
      toast.error('No job card number available');
      return;
    }

    try {
      setLoading(true);

      // Fetch local MMS activities with attachments using the API service
      const activitiesData = await getJobActivities(job.JOBCARD_NO);
      const list = activitiesData?.data?.activities ?? activitiesData?.activities ?? [];
      setActivities(list);

      // If the backend doesn't include worker_name, try load local workers for a client-side name lookup.
      const needsWorkerLookup = list.some((a) => !a?.worker_name && a?.worker_id);
      if (needsWorkerLookup && workers.length === 0 && !loadingWorkers) {
        fetchWorkers();
      }
    } catch (err) {
      console.error('Error fetching activities:', err);
      toast.error('Failed to load job activity');
      // Mark as fetched to prevent infinite retries/spinner loops
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [fetchWorkers, job.JOBCARD_NO, loadingWorkers, workers.length]);

  const getActivityActorName = (activity) => {
    // Check created_by_name first (portal user who created the activity)
    const createdBy = activity?.created_by_name;
    if (createdBy && String(createdBy).trim() && !/unknown user/i.test(createdBy)) {
      return String(createdBy).trim();
    }

    // Check worker_name (field worker)
    const direct = activity?.worker_name;
    if (direct && String(direct).trim() && !/unknown user/i.test(direct)) {
      return String(direct).trim();
    }

    const workerId = activity?.worker_id != null ? String(activity.worker_id) : null;
    if (workerId) {
      const w = workersById.get(workerId);
      if (w?.worker_name) return String(w.worker_name).trim();
    }

    const desc = activity?.description ? String(activity.description) : '';
    // If the description already includes a name, extract it.
    const match = desc.match(/\bby\s+([^\n\r]+)\s*$/i);
    if (match && match[1] && !/unknown user/i.test(match[1])) {
      return match[1].trim();
    }

    return null;
  };

  const getActivityDisplayDescription = (activity) => {
    const desc = activity?.description ? String(activity.description) : '';
    const actor = getActivityActorName(activity);
    if (actor && !/unknown user/i.test(actor)) {
      return desc.replace(/\bby\s+Unknown User\b/gi, `by ${actor}`);
    }
    return desc;
  };

  useEffect(() => {
    if (activeTab === 'events' && activities === null && !loading) {
      fetchActivities();
    }
    
    // Fetch raw coordinates when map tab is activated
    if (activeTab === 'map' && !rawCoordinates && !loadingRawCoords && job.PROPERTY_ID) {
      fetchRawCoordinates();
    }
    
    // Fetch workers when assignment tab is activated
    if (activeTab === 'assign' && workers.length === 0 && !loadingWorkers) {
      fetchWorkers();
    }
  }, [
    activeTab,
    activities,
    fetchActivities,
    fetchRawCoordinates,
    fetchWorkers,
    job.PROPERTY_ID,
    loading,
    loadingRawCoords,
    loadingWorkers,
    rawCoordinates,
    workers.length
  ]);

  const handleAssignJob = async () => {
    if (!selectedWorker) {
      toast.error('Please select a worker');
      return;
    }

    try {
      setSubmittingAssignment(true);

      const assignmentData = {
        worker_id: selectedWorker,
        jobcard_no: job.JOBCARD_NO,
        application_id: job.APPLICATION_ID, // Required by backend
        assigned_by: 'admin', // You can get this from auth context
        status: 'active',
        notes: assignmentNotes || `Assigned job card ${job.JOBCARD_NO}`,
        // Include property and location if available
        ...(job.PROPERTY_ID && { property_id: job.PROPERTY_ID }),
        ...(job.LATITUDE && job.LONGITUDE && {
          latitude: job.LATITUDE,
          longitude: job.LONGITUDE
        })
      };

      await createAssignment(assignmentData);
      
      toast.success('Job assigned successfully!');
      
      // Reset form
      setSelectedWorker('');
      setAssignmentNotes('');
      
      // Refresh parent if callback provided
      if (onRefresh) {
        onRefresh();
      }
      
    } catch (err) {
      console.error('Error assigning job:', err);
      toast.error(err.message || 'Failed to assign job');
    } finally {
      setSubmittingAssignment(false);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    // Validate file types and sizes
    const validFiles = [];
    const errors = [];
    
    selectedFiles.forEach(file => {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      
      if (!isImage && !isVideo) {
        errors.push(`${file.name}: Invalid file type. Only images and videos allowed.`);
        return;
      }
      
      const maxSize = 50 * 1024 * 1024; // 50MB
      if (file.size > maxSize) {
        errors.push(`${file.name}: File too large (max 50MB)`);
        return;
      }
      
      validFiles.push(file);
    });
    
    if (errors.length > 0) {
      toast.error(errors.join('\n'));
    }
    
    setActivityForm(prev => ({
      ...prev,
      files: [...prev.files, ...validFiles]
    }));
  };

  const removeFile = (index) => {
    setActivityForm(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const handleSubmitActivity = async (e) => {
    e.preventDefault();
    
    if (!activityForm.description.trim()) {
      toast.error('Please enter a description');
      return;
    }
    
    try {
      setSubmittingActivity(true);
      
      const formData = new FormData();
      formData.append('jobcard_no', job.JOBCARD_NO);
      formData.append('activity_type', activityForm.activity_type);
      formData.append('description', activityForm.description);
      
      // Append files
      activityForm.files.forEach(file => {
        formData.append('files[]', file);
      });
      
      // Use the API service which handles authentication
      const result = await addJobActivity(formData);
      
      toast.success(`Activity logged successfully. ${result.files_uploaded || 0} file(s) uploaded.`);
      
      // Reset form
      setActivityForm({
        description: '',
        activity_type: 'field_note',
        files: []
      });
      
      // Refresh activities
      await fetchActivities();
    } catch (err) {
      console.error('Error submitting activity:', err);
      toast.error(err.message || 'Failed to log activity');
    } finally {
      setSubmittingActivity(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-ZA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      0: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
      1: { label: 'Assigned', color: 'bg-blue-100 text-blue-800 border-blue-300' },
      3: { label: 'In Progress', color: 'bg-purple-100 text-purple-800 border-purple-300' },
      4: { label: 'Scheduled', color: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
      5: { label: 'On Hold', color: 'bg-orange-100 text-orange-800 border-orange-300' },
      6: { label: 'Completed', color: 'bg-green-100 text-green-800 border-green-300' }
    };
    
    const statusInfo = statusMap[status] || { 
      label: getOmStatusName(status), 
      color: 'bg-gray-100 text-gray-800 border-gray-300' 
    };
    
    return (
      <span className={`inline-flex px-3 py-1.5 text-sm font-medium rounded-full border ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FileText },
    { id: 'events', label: 'Events & History', icon: Activity },
    { id: 'customer', label: 'Customer', icon: User },
    { id: 'property', label: 'Property', icon: Home },
    { id: 'map', label: 'Location Map', icon: MapIcon },
    { id: 'assign', label: 'Assign to Worker', icon: UserPlus }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-blue-50 to-white">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 rounded-lg p-2" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
              <Briefcase className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Job Card #{job.JOBCARD_NO}</h2>
              <p className="text-sm text-gray-500">Application ID: {job.APPLICATION_ID}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 bg-gray-50">
          <div className="px-6">
            <nav className="flex space-x-8" aria-label="Tabs">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors
                      ${activeTab === tab.id
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Status Row */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Current Status</h3>
                  {getStatusBadge(job.OM_STATUS)}
                </div>
                {job.PRIORITY && (
                  <div className="text-right">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Priority</h3>
                    <span className="text-lg font-semibold text-gray-900">
                      {getPriorityName(job.PRIORITY_ID || job.PRIORITY_RANK)}
                    </span>
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                  <FileText className="h-4 w-4 mr-2 text-blue-600" />
                  Job Description
                </h3>
                <p className="text-gray-900">
                  {job.JOB_DESCRIPTION || 'No description available'}
                </p>
              </div>

              {/* Key Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Application Type</h4>
                  <p className="text-sm text-gray-900 font-medium">
                    {getApplicationTypeName(job.APPLICATION_TYPE_ID)}
                  </p>
                  {job.APPLICATION_TYPE_DESC && (
                    <p className="text-xs text-gray-500 mt-1">{job.APPLICATION_TYPE_DESC}</p>
                  )}
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Station/Depot</h4>
                  <p className="text-sm text-gray-900 font-medium">
                    {getStationName(job.STATION_NO)}
                  </p>
                  {job.STATION_CODE && (
                    <p className="text-xs text-gray-500 mt-1">Code: {job.STATION_CODE}</p>
                  )}
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    <Clock className="h-3 w-3 inline mr-1" />
                    Issued Date
                  </h4>
                  <p className="text-sm text-gray-900 font-medium">{formatDate(job.ISSUED_DATETIME)}</p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    <Clock className="h-3 w-3 inline mr-1" />
                    SLA Date
                  </h4>
                  <p className="text-sm text-gray-900 font-medium">{formatDate(job.SLA_DATE)}</p>
                </div>
              </div>

              {/* Assignment Info */}
              {job.ASSIGNED_TO_NAME && (
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                    <User className="h-4 w-4 mr-2 text-green-600" />
                    Assignment Details
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Assigned To</p>
                      <p className="text-sm font-medium text-gray-900">{job.ASSIGNED_TO_NAME}</p>
                    </div>
                    {job.ASSIGNED_TO_EMAIL && (
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Email</p>
                        <p className="text-sm text-gray-900">{job.ASSIGNED_TO_EMAIL}</p>
                      </div>
                    )}
                    {job.ASSIGNED_TO_PHONE && (
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Phone</p>
                        <p className="text-sm text-gray-900">{job.ASSIGNED_TO_PHONE}</p>
                      </div>
                    )}
                    {job.ASSIGN_DATE && (
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Assignment Date</p>
                        <p className="text-sm text-gray-900">{formatDate(job.ASSIGN_DATE)}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Outcome Info */}
              {job.JC_OUTCOME_NAME && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Outcome</h3>
                  <p className="text-sm text-gray-900 font-medium">{job.JC_OUTCOME_NAME}</p>
                  {job.JC_NOTE_DESCRIPTION && job.JC_NOTE_DESCRIPTION !== '*Current*' && (
                    <p className="text-xs text-gray-600 mt-2">{job.JC_NOTE_DESCRIPTION}</p>
                  )}
                </div>
              )}

              {/* Location */}
              {(job.LATITUDE && job.LONGITUDE) && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <MapPin className="h-4 w-4 mr-2 text-blue-600" />
                    Location
                  </h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Coordinates</p>
                      <p className="text-sm text-gray-900 font-mono">{job.LATITUDE}, {job.LONGITUDE}</p>
                    </div>
                    <button
                      onClick={() => window.open(`https://www.google.com/maps?q=${job.LATITUDE},${job.LONGITUDE}`, '_blank')}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition-colors"
                    >
                      Open in Maps
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Events Tab */}
          {activeTab === 'events' && (
            <div className="space-y-6">
              {/* Activity Logging Form */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center">
                  <Activity className="h-4 w-4 mr-2 text-blue-600" />
                  Log New Activity
                </h3>
                <form onSubmit={handleSubmitActivity} className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Activity Type
                    </label>
                    <select
                      value={activityForm.activity_type}
                      onChange={(e) => setActivityForm(prev => ({ ...prev, activity_type: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="field_note">Field Note</option>
                      <option value="progress_update">Progress Update</option>
                      <option value="completion">Completion</option>
                      <option value="issue">Issue/Problem</option>
                      <option value="customer_interaction">Customer Interaction</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description *
                    </label>
                    <textarea
                      value={activityForm.description}
                      onChange={(e) => setActivityForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Enter activity description..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Attachments (Images/Videos)
                    </label>
                    <div className="space-y-2">
                      <input
                        type="file"
                        accept="image/*,video/*"
                        multiple
                        onChange={handleFileSelect}
                        className="hidden"
                        id="activity-file-input"
                      />
                      <label
                        htmlFor="activity-file-input"
                        className="flex items-center justify-center px-4 py-2 border-2 border-dashed border-gray-300 rounded-md cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                      >
                        <Upload className="h-5 w-5 mr-2 text-gray-400" />
                        <span className="text-sm text-gray-600">Click to select files</span>
                      </label>
                      
                      {activityForm.files.length > 0 && (
                        <div className="space-y-1">
                          {activityForm.files.map((file, index) => {
                            const isImage = file.type.startsWith('image/');
                            const isVideo = file.type.startsWith('video/');
                            return (
                              <div key={index} className="flex items-center justify-between bg-white px-3 py-2 rounded border border-gray-200">
                                <div className="flex items-center space-x-2">
                                  {isImage && <ImageIcon className="h-4 w-4 text-green-500" />}
                                  {isVideo && <Video className="h-4 w-4 text-purple-500" />}
                                  <span className="text-sm text-gray-700">{file.name}</span>
                                  <span className="text-xs text-gray-500">
                                    ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeFile(index)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={submittingActivity || !activityForm.description.trim()}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                  >
                    {submittingActivity ? 'Logging Activity...' : 'Log Activity'}
                  </button>
                </form>
              </div>

              <h3 className="text-lg font-bold text-gray-900">Activity History</h3>
              
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Loading history...</span>
                </div>
              ) : (
                <>
                  {/* MMS Activities with Attachments */}
                  {Array.isArray(activities) && activities.length > 0 && (
                    <div className="space-y-4">
                      <h4 className="text-sm font-semibold text-gray-700">Field Activities</h4>
                      {activities.map((activity, index) => {
                        const actorName = getActivityActorName(activity);
                        return (
                        <div key={`activity-${activity.activity_id || activity.id || index}`} className="bg-white border border-blue-200 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="inline-flex px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800">
                                  {activity.activity_type?.replace('_', ' ').toUpperCase() || 'ACTIVITY'}
                                </span>
                                {actorName && (
                                  <span className="text-xs text-gray-500">by {actorName}</span>
                                )}
                              </div>
                              <p className="text-sm text-gray-900 whitespace-pre-wrap">{getActivityDisplayDescription(activity)}</p>
                              
                              {/* Display Attachments */}
                              {activity.attachments && activity.attachments.length > 0 && (
                                <div className="mt-3">
                                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                    {activity.attachments.map((attachment, attIndex) => {
                                      const fileUrl = `${API_BASE_URL}/api/mms/activities/attachments/${attachment.file_path}`;
                                      const isImage = attachment.is_image === 1;
                                      
                                      if (isImage) {
                                        return (
                                          <div
                                            key={attIndex}
                                            className="group relative bg-gray-100 rounded border overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                                            onClick={() => window.open(fileUrl, '_blank')}
                                          >
                                            <div className="aspect-square bg-gray-200 flex items-center justify-center">
                                              <img
                                                src={fileUrl}
                                                alt={attachment.file_name}
                                                className="w-full h-full object-cover hover:scale-105 transition-transform"
                                                onError={(e) => {
                                                  e.target.style.display = 'none';
                                                  e.target.parentElement.innerHTML = '<div class="text-red-500 text-xs">Error loading</div>';
                                                }}
                                              />
                                            </div>
                                            <div className="p-1 bg-white">
                                              <p className="text-xs text-gray-600 truncate" title={attachment.file_name}>
                                                {attachment.file_name}
                                              </p>
                                            </div>
                                          </div>
                                        );
                                      } else {
                                        // Video
                                        return (
                                          <div
                                            key={attIndex}
                                            className="group relative bg-gray-100 rounded border overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                                            onClick={() => window.open(fileUrl, '_blank')}
                                          >
                                            <div className="aspect-square bg-gray-800 flex items-center justify-center">
                                              <Video className="h-8 w-8 text-white" />
                                            </div>
                                            <div className="p-1 bg-white">
                                              <p className="text-xs text-gray-600 truncate" title={attachment.file_name}>
                                                {attachment.file_name}
                                              </p>
                                            </div>
                                          </div>
                                        );
                                      }
                                    })}
                                  </div>
                                </div>
                              )}
                              
                              {activity.latitude && activity.longitude && (
                                <p className="text-xs text-gray-500 mt-2">
                                  📍 Location: {activity.latitude}, {activity.longitude}
                                </p>
                              )}
                            </div>
                            <div className="text-right ml-4">
                              <span className="text-xs text-gray-500 block">{formatDate(activity.created_at)}</span>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )}

                  {Array.isArray(activities) && activities.length === 0 && (
                    <div className="text-center py-12">
                      <Activity className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600">No activities recorded yet</p>
                      <p className="text-sm text-gray-500 mt-1">Use the form above to log a new activity</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Customer Tab */}
          {activeTab === 'customer' && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold text-gray-900 flex items-center">
                <User className="h-5 w-5 mr-2 text-blue-600" />
                Customer Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Full Name</h4>
                  <p className="text-lg font-medium text-gray-900">{job.CUSTOMER_NAME || 'Unknown'}</p>
                </div>

                {job.CELL_TEL_NO && (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center">
                      <Phone className="h-3 w-3 mr-1" />
                      Phone Number
                    </h4>
                    <p className="text-lg font-medium text-gray-900">{job.CELL_TEL_NO}</p>
                    <a href={`tel:${job.CELL_TEL_NO}`} className="text-sm text-blue-600 hover:underline mt-1 block">
                      Call Now
                    </a>
                  </div>
                )}

                {job.CUSTOMER_EMAIL && (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Email Address</h4>
                    <p className="text-sm text-gray-900">{job.CUSTOMER_EMAIL}</p>
                    <a href={`mailto:${job.CUSTOMER_EMAIL}`} className="text-sm text-blue-600 hover:underline mt-1 block">
                      Send Email
                    </a>
                  </div>
                )}

                {job.ACCOUNT_NUMBER && (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Account Number</h4>
                    <p className="text-lg font-medium text-gray-900 font-mono">{job.ACCOUNT_NUMBER}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Property Tab */}
          {activeTab === 'property' && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold text-gray-900 flex items-center">
                <Home className="h-5 w-5 mr-2 text-blue-600" />
                Property Details
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Property Reference</h4>
                  <p className="text-lg font-medium text-gray-900">{job.PROP_REF || 'N/A'}</p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Stand Number</h4>
                  <p className="text-lg font-medium text-gray-900">{job.STAND_NO || 'N/A'}</p>
                </div>

                {job.ADDRESS && (
                  <div className="col-span-1 md:col-span-2 bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center">
                      <MapPin className="h-3 w-3 mr-1" />
                      Physical Address
                    </h4>
                    <p className="text-base text-gray-900">{job.ADDRESS}</p>
                  </div>
                )}

                {job.PROPERTY_ID && (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Property ID</h4>
                    <p className="text-lg font-medium text-gray-900 font-mono">{job.PROPERTY_ID}</p>
                  </div>
                )}

                {(job.LATITUDE && job.LONGITUDE) && (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">GPS Coordinates</h4>
                    <p className="text-sm text-gray-900 font-mono mb-2">{job.LATITUDE}, {job.LONGITUDE}</p>
                    <button
                      onClick={() => window.open(`https://www.google.com/maps?q=${job.LATITUDE},${job.LONGITUDE}`, '_blank')}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      View on Google Maps →
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Map Tab */}
          {activeTab === 'map' && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center">
                <MapIcon className="h-5 w-5 mr-2 text-blue-600" />
                Job Location Map
              </h3>

              {/* Map Container - Always render with overlays */}
              <div className="border border-gray-200 rounded-lg overflow-hidden relative" style={{ height: '600px' }}>
                <MapContainer
                  center={
                    (job.LATITUDE && job.LONGITUDE)
                      ? [parseFloat(job.LATITUDE), parseFloat(job.LONGITUDE)]
                      : [-25.4753, 30.9700] // Default to Mbombela city center
                  }
                  zoom={(job.LATITUDE && job.LONGITUDE) ? 16 : 12}
                  style={{ height: '100%', width: '100%' }}
                  scrollWheelZoom={true}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                    url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                    maxZoom={19}
                    subdomains={['a', 'b', 'c', 'd']}
                  />
                  
                  {/* Only show marker if coordinates exist */}
                  {(job.LATITUDE && job.LONGITUDE) && (
                    <Marker position={[parseFloat(job.LATITUDE), parseFloat(job.LONGITUDE)]}>
                      <Popup>
                        <div className="p-2">
                          <h4 className="font-bold text-sm mb-1">Job #{job.JOBCARD_NO}</h4>
                          {job.ADDRESS && (
                            <p className="text-xs text-gray-600 mb-2">{job.ADDRESS}</p>
                          )}
                          {job.JOB_DESCRIPTION && (
                            <p className="text-xs text-gray-700 mb-2">{job.JOB_DESCRIPTION}</p>
                          )}
                          {job.ASSIGNED_TO_NAME && (
                            <p className="text-xs text-gray-600">
                              <User className="h-3 w-3 inline mr-1" />
                              Assigned to: {job.ASSIGNED_TO_NAME}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mt-2 font-mono">
                            {parseFloat(job.LATITUDE).toFixed(6)}, {parseFloat(job.LONGITUDE).toFixed(6)}
                          </p>
                        </div>
                      </Popup>
                    </Marker>
                  )}
                </MapContainer>

                {/* Coordinates Info Overlay - Top Left */}
                {(job.LATITUDE && job.LONGITUDE) && (
                  <div className="absolute top-4 left-4 z-[1000] bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-md">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                      <MapPin className="h-4 w-4 mr-1 text-blue-600" />
                      GPS Coordinates (WGS84)
                    </h4>
                    <p className="text-sm text-gray-900 font-mono mb-2">
                      {parseFloat(job.LATITUDE).toFixed(6)}, {parseFloat(job.LONGITUDE).toFixed(6)}
                    </p>
                    {job.ADDRESS && (
                      <p className="text-xs text-gray-600 mb-3">
                        {job.ADDRESS}
                      </p>
                    )}
                    
                    {/* Raw EDAMS Data */}
                    {rawCoordinates && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs font-semibold text-gray-600 mb-2">Raw EDAMS Data:</p>
                        
                        {rawCoordinates.success === false ? (
                          <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
                            <p className="text-xs text-yellow-800">
                              EDAMS database unavailable - raw coordinate data cannot be displayed
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-1.5 text-xs">
                            {rawCoordinates.plot_points && (
                              <div className="bg-gray-50 rounded p-2">
                                <p className="font-medium text-gray-700 mb-1">PLOT_DTL_POINTS (Sample)</p>
                                <p className="font-mono text-gray-600">
                                  X: {rawCoordinates.plot_points.sample_point.POINT_X}
                                </p>
                                <p className="font-mono text-gray-600">
                                  Y: {rawCoordinates.plot_points.sample_point.POINT_Y}
                                </p>
                                {rawCoordinates.plot_points.count > 1 && (
                                  <p className="text-gray-500 mt-1">({rawCoordinates.plot_points.count} points total)</p>
                                )}
                              </div>
                            )}
                            
                            {rawCoordinates.caller_log && (
                              <div className="bg-gray-50 rounded p-2">
                                <p className="font-medium text-gray-700 mb-1">CS_CALLERLOG</p>
                                <p className="font-mono text-gray-600">
                                  X: {rawCoordinates.caller_log.LOCATION_X}
                                </p>
                                <p className="font-mono text-gray-600">
                                  Y: {rawCoordinates.caller_log.LOCATION_Y}
                                </p>
                              </div>
                            )}
                            
                            {rawCoordinates.problem_log && (
                              <div className="bg-gray-50 rounded p-2">
                                <p className="font-medium text-gray-700 mb-1">PROBLEM_LOG</p>
                                <p className="font-mono text-gray-600">
                                  X: {rawCoordinates.problem_log.LOCATION_X}
                                </p>
                                <p className="font-mono text-gray-600">
                                  Y: {rawCoordinates.problem_log.LOCATION_Y}
                                </p>
                              </div>
                            )}
                            
                            {!rawCoordinates.plot_points && !rawCoordinates.caller_log && !rawCoordinates.problem_log && (
                              <p className="text-gray-500 italic">No raw EDAMS coordinate data available</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {loadingRawCoords && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs text-gray-500 italic">Loading raw data...</p>
                      </div>
                    )}
                    
                    <button
                      onClick={() => window.open(`https://www.google.com/maps?q=${job.LATITUDE},${job.LONGITUDE}`, '_blank')}
                      className="w-full px-3 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors mt-3"
                    >
                      Open in Google Maps
                    </button>
                  </div>
                )}

                {/* Property Details Overlay - Top Right */}
                {(job.LATITUDE && job.LONGITUDE) && (job.STAND_NO || job.PROP_REF || job.STATION_NAME || job.PROPERTY_ID) && (
                  <div className="absolute top-4 right-4 z-[1000] bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-xs">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                      <Home className="h-4 w-4 mr-1 text-blue-600" />
                      Property Details
                    </h4>
                    <div className="space-y-2 text-xs">
                      {job.STAND_NO && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Stand:</span>
                          <span className="font-medium text-gray-900">{job.STAND_NO}</span>
                        </div>
                      )}
                      {job.PROP_REF && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Reference:</span>
                          <span className="font-medium text-gray-900">{job.PROP_REF}</span>
                        </div>
                      )}
                      {job.STATION_NAME && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Station:</span>
                          <span className="font-medium text-gray-900">{job.STATION_NAME}</span>
                        </div>
                      )}
                      {job.PROPERTY_ID && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Property ID:</span>
                          <span className="font-medium text-gray-900 font-mono">{job.PROPERTY_ID}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* No Data Overlay - Center - Only show if no coordinates */}
                {!(job.LATITUDE && job.LONGITUDE) && (
                  <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-[1000] pointer-events-none">
                    <div className="text-center p-8">
                      <MapPin className="h-16 w-16 text-gray-400 mb-4 mx-auto" />
                      <h4 className="text-lg font-medium text-gray-900 mb-2">No Location Data Available</h4>
                      <p className="text-sm text-gray-500 text-center max-w-md">
                        This job card does not have GPS coordinates assigned. Location data is typically added when:
                      </p>
                      <ul className="text-sm text-gray-500 mt-3 space-y-1 text-left inline-block">
                        <li>• The job is linked to a property with coordinate data</li>
                        <li>• Coordinates are captured during job assignment</li>
                        <li>• Artisans share their location when updating the job</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Assignment Tab */}
          {activeTab === 'assign' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <UserPlus className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
                  <div>
                    <h3 className="text-sm font-semibold text-blue-900 mb-1">Assign Job to Worker</h3>
                    <p className="text-sm text-blue-700">
                      Assign this job card to a local MMS worker account. The worker will receive the assignment on their mobile app.
                    </p>
                  </div>
                </div>
              </div>

              {/* Job Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Job Summary</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Job Card:</span>
                    <span className="ml-2 font-medium text-gray-900">{job.JOBCARD_NO}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Status:</span>
                    <span className="ml-2">{getStatusBadge(job.OM_STATUS)}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-500">Description:</span>
                    <p className="mt-1 text-gray-900">{job.JOB_DESCRIPTION || 'N/A'}</p>
                  </div>
                  {job.ADDRESS && (
                    <div className="col-span-2">
                      <span className="text-gray-500">Location:</span>
                      <p className="mt-1 text-gray-900 flex items-start">
                        <MapPin className="h-4 w-4 mr-1 mt-0.5 text-gray-400" />
                        {job.ADDRESS}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Worker Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Worker *
                </label>
                {loadingWorkers ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-600">Loading workers...</span>
                  </div>
                ) : workers.length === 0 ? (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                    <p className="text-sm text-yellow-800">No workers available. Please create worker accounts first.</p>
                  </div>
                ) : (
                  <select
                    value={selectedWorker}
                    onChange={(e) => setSelectedWorker(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={submittingAssignment}
                  >
                    <option value="">-- Select a worker --</option>
                    {workers.map(worker => (
                      <option key={worker.worker_id} value={worker.worker_id}>
                        {worker.worker_name} 
                        {worker.worker_phone && ` (${worker.worker_phone})`}
                        {worker.active_jobs > 0 && ` - ${worker.active_jobs} active job(s)`}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Worker Details */}
              {selectedWorker && workers.find(w => w.worker_id === selectedWorker) && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  {(() => {
                    const worker = workers.find(w => w.worker_id === selectedWorker);
                    return (
                      <div className="space-y-2 text-sm">
                        <h4 className="font-semibold text-green-900 mb-2">Selected Worker Details</h4>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <span className="text-green-700">Name:</span>
                            <span className="ml-2 font-medium text-green-900">{worker.worker_name}</span>
                          </div>
                          {worker.worker_phone && (
                            <div>
                              <span className="text-green-700">Phone:</span>
                              <span className="ml-2 font-medium text-green-900">{worker.worker_phone}</span>
                            </div>
                          )}
                          {worker.worker_email && (
                            <div>
                              <span className="text-green-700">Email:</span>
                              <span className="ml-2 font-medium text-green-900">{worker.worker_email}</span>
                            </div>
                          )}
                          <div>
                            <span className="text-green-700">Active Jobs:</span>
                            <span className="ml-2 font-medium text-green-900">{worker.active_jobs || 0}</span>
                          </div>
                          {worker.worker_employee_id && (
                            <div>
                              <span className="text-green-700">Employee ID:</span>
                              <span className="ml-2 font-medium text-green-900">{worker.worker_employee_id}</span>
                            </div>
                          )}
                          <div>
                            <span className="text-green-700">Status:</span>
                            <span className={`ml-2 font-medium ${worker.worker_status === '1' ? 'text-green-900' : 'text-red-900'}`}>
                              {worker.worker_status === '1' ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Assignment Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assignment Notes (Optional)
                </label>
                <textarea
                  value={assignmentNotes}
                  onChange={(e) => setAssignmentNotes(e.target.value)}
                  placeholder="Add any special instructions or notes for the worker..."
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={submittingAssignment}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setSelectedWorker('');
                    setAssignmentNotes('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  disabled={submittingAssignment}
                >
                  Clear
                </button>
                <button
                  onClick={handleAssignJob}
                  disabled={!selectedWorker || submittingAssignment}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                >
                  {submittingAssignment ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Assigning...
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Assign Job
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobCardDetailModal;
